import type {SvgIconComponent} from "@mui/icons-material";
import {
    CategoryOutlined,
    Inventory2Outlined,
    MemoryOutlined,
    MonetizationOnOutlined,
    PrecisionManufacturingOutlined,
    SupportAgentOutlined,
} from "@mui/icons-material";
import type {IconWrapperColor} from "~/components/IconWrapper/IconWrapper";
import {humanizeCode} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/valueChainLayout";

export type SupplyTypeStyle = {
    label: string;
    Icon: SvgIconComponent;
    /** Drawn with the shared `IconWrapper` in this colour. */
    color: IconWrapperColor;
};

/** The supply types seen in real provider data (free text in the API, §2.17). */
const KNOWN: Record<string, Omit<SupplyTypeStyle, 'label'>> = {
    production_support_service: {Icon: PrecisionManufacturingOutlined, color: 'warning'},
    indirect_support_service: {Icon: SupportAgentOutlined, color: 'primary'},
};

/**
 * Hardcoded for now: the exact codes for these aren't documented yet, so they match on a keyword
 * (e.g. `financial_service`, `technology_provider`). Exact {@link KNOWN} codes win.
 */
const BY_KEYWORD: Array<{keyword: string} & Omit<SupplyTypeStyle, 'label'>> = [
    {keyword: 'financ', Icon: MonetizationOnOutlined, color: 'info'},
    {keyword: 'raw_material', Icon: Inventory2Outlined, color: 'warning'},
    {keyword: 'technolog', Icon: MemoryOutlined, color: 'secondary'},
];

/** Anything else still gets a distinct colour, picked stably from the code. */
const FALLBACK_COLORS: IconWrapperColor[] = ['success', 'error'];

export function supplyTypeStyle(code: string): SupplyTypeStyle {
    const label = humanizeCode(code) || 'Unspecified';
    const known = KNOWN[code];
    if (known) return {label, ...known};
    const lower = code.toLowerCase();
    const byKeyword = BY_KEYWORD.find(entry => lower.includes(entry.keyword));
    if (byKeyword) return {label, Icon: byKeyword.Icon, color: byKeyword.color};
    if (!code) return {label, Icon: CategoryOutlined, color: 'default'};
    const hash = [...code].reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return {label, Icon: CategoryOutlined, color: FALLBACK_COLORS[hash % FALLBACK_COLORS.length]};
}
